import React, { useState } from "react";
import { FaPlus, FaTrash, FaInfoCircle } from "react-icons/fa";
import { FiChevronRight } from "react-icons/fi";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion";
import Footer from "../../components/footer";
import FooterButton from "../../components/footerButton";
import WarningModal from "../../components/clearWarningModal";
import { ClipLoader } from "react-spinners";
import useCreateFunctionalChecklist from "../../hooks/createFunctionalChecklist";
import GetScoringScales from "../../hooks/getScoringScales";
import {
  DEFAULT_SCORING_SCALE_NAME,
  getScaleValues,
  getScorePillProps,
} from "../../utils/scoringScale";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

// Clear button component
const Item1 = ({ isOpen, setIsOpen, onClear }) => (
  <>
    <button
      className="btn btn_danger px-5 mt-3 mt-sm-0"
      onClick={() => setIsOpen(true)}
      data-toggle="modal"
      data-target="#exampleModal"
    >
      <small>CLEAR</small>
    </button>
    <WarningModal setIsOpen={setIsOpen} isOpen={isOpen} onConfirm={onClear} />
  </>
);

// Save and Publish buttons component
const Item2 = ({ onSaveForLater, onPublish, isPending }) => {
  return (
    <div className="flex-grow-1 flex-grow-sm-0 d-flex justify-content-between justify-content-sm-end">
      {isPending && (
        <button className="btn btn_outline_light px-3 mx-0 mx-md-3 d-flex justify-content-between">
          <ClipLoader
            size={20}
            isLoading={true}
            className="mr-2"
            color="#fff"
          />
          <small>SAVE FOR LATER</small>
        </button>
      )}
      {!isPending && (
        <button
          className="btn btn_outline_light px-3 mx-0 mx-md-3"
          onClick={onSaveForLater}
        >
          <small>SAVE FOR LATER</small>
        </button>
      )}
      <button onClick={onPublish} className="btn btn_light pl-4 pr-2 ml-3">
        <small className="mx-3">PUBLISH</small>
      </button>
    </div>
  );
};

const CreateCustomFunctionalChecklist = () => {
  const [name, setName] = useState("");
  // scoringScaleId left empty means "the default scale" — the API applies the
  // same fallback, so a heading nobody touches is scored 1-5 exactly as before
  // scales existed.
  const [headings, setHeadings] = useState([
    {
      id: 1,
      heading: "",
      orderIndex: 0,
      scoringScaleId: "",
      subheadings: [
        {
          id: 1,
          name: "",
          definition: "",
        },
      ],
    },
  ]);
  const [currentUuid, setCurrentUuid] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isPending, setIsPending] = useState(false);

  // React Query hook for creating functional checklist
  const createChecklistMutation = useCreateFunctionalChecklist();

  // The scales this organization may pick from: the built-ins plus its own.
  const { data: scoringScalesResponse } = GetScoringScales();
  const scoringScales = scoringScalesResponse?.data || [];
  const findScale = (scaleId) =>
    scoringScales.find((scale) => String(scale.id) === String(scaleId));

  // Navigation hook
  const history = useHistory();

  // Add a new heading
  const addHeading = () => {
    const newHeading = {
      id: headings.length + 1,
      heading: "",
      orderIndex: headings.length,
      scoringScaleId: "",
      subheadings: [
        {
          id: 1,
          name: "",
          definition: "",
        },
      ],
    };
    setHeadings([...headings, newHeading]);
    setCurrentUuid(headings.length); // Open the new heading
  };

  // Remove a heading
  const removeHeading = (headingIndex) => {
    const updatedHeadings = headings
      .filter((_, index) => index !== headingIndex)
      .map((heading, index) => ({
        ...heading,
        orderIndex: index,
      }));
    setHeadings(updatedHeadings);
    // Adjust current UUID if necessary
    if (currentUuid >= updatedHeadings.length) {
      setCurrentUuid(Math.max(0, updatedHeadings.length - 1));
    }
  };

  // Update heading name
  const updateHeadingName = (headingIndex, heading) => {
    const updatedHeadings = [...headings];
    updatedHeadings[headingIndex].heading = heading;
    setHeadings(updatedHeadings);
  };

  // The scale this heading's scores are recorded on.
  const updateHeadingScoringScale = (headingIndex, scoringScaleId) => {
    const updatedHeadings = [...headings];
    updatedHeadings[headingIndex].scoringScaleId = scoringScaleId;
    setHeadings(updatedHeadings);
  };

  // Add a new functional outcome (subheading) to a specific heading
  const addFunctionalOutcome = (headingIndex) => {
    const updatedHeadings = [...headings];
    const newSubheading = {
      id: updatedHeadings[headingIndex].subheadings.length + 1,
      name: "",
      definition: "",
    };
    updatedHeadings[headingIndex].subheadings.push(newSubheading);
    setHeadings(updatedHeadings);
  };

  // Remove a functional outcome (subheading)
  const removeFunctionalOutcome = (headingIndex, subheadingIndex) => {
    const updatedHeadings = [...headings];
    updatedHeadings[headingIndex].subheadings = updatedHeadings[
      headingIndex
    ].subheadings.filter((_, index) => index !== subheadingIndex);
    setHeadings(updatedHeadings);
  };

  // Update subheading name
  const updateSubheadingName = (headingIndex, subheadingIndex, name) => {
    const updatedHeadings = [...headings];
    updatedHeadings[headingIndex].subheadings[subheadingIndex].name = name;
    setHeadings(updatedHeadings);
  };

  // Update subheading definition
  const updateSubheadingDefinition = (
    headingIndex,
    subheadingIndex,
    definition
  ) => {
    const updatedHeadings = [...headings];
    updatedHeadings[headingIndex].subheadings[subheadingIndex].definition =
      definition;
    setHeadings(updatedHeadings);
  };

  // Clear all data
  const handleClear = () => {
    setName("");
    setHeadings([
      {
        id: 1,
        heading: "",
        orderIndex: 0,
        scoringScaleId: "",
        subheadings: [
          {
            id: 1,
            name: "",
            definition: "",
          },
        ],
      },
    ]);
    setCurrentUuid(0);
    setIsOpen(false);
  };

  // Helper function to prepare checklist data
  const prepareChecklistData = (status) => {
    // Get organization ID from localStorage
    const user = JSON.parse(localStorage.getItem("user"));
    const organizationId = user?.organizations?.[0]?.id;

    return {
      name,
      // A heading with no scale picked sends no scoringScaleId at all: the API
      // reads that as the default scale, and an unknown id would be a 404.
      headings: headings.map(({ scoringScaleId, ...heading }) =>
        scoringScaleId
          ? { ...heading, scoringScaleId: Number(scoringScaleId) }
          : heading
      ),
      status,
      targetType: "ORGANIZATION",
      targetId: organizationId,
    };
  };

  // Validation function
  const validateChecklist = () => {
    if (!name.trim()) {
      toast.error("Please enter a checklist title");
      return false;
    }

    // Check if at least one heading has content
    const hasValidHeadings = headings.some((heading) => heading.heading.trim());
    if (!hasValidHeadings) {
      toast.error("Please enter at least one heading");
      return false;
    }

    return true;
  };

  // Save for later
  const handleSaveForLater = async () => {
    if (!validateChecklist()) {
      return;
    }

    setIsPending(true);

    try {
      const checklistData = prepareChecklistData("DRAFT");
      await createChecklistMutation.mutateAsync(checklistData);

      toast.success("Checklist saved as draft successfully!");
      // Redirect to manage functional checklists page
      history.push("/admin/manage_functional_checklists");
    } catch (error) {
      console.error("Failed to save checklist:", error);
      toast.error("Failed to save checklist. Please try again.");
    } finally {
      setIsPending(false);
    }
  };

  // Publish checklist
  const handlePublish = async () => {
    if (!validateChecklist()) {
      return;
    }

    // Additional validation for publishing
    const hasCompleteHeadings = headings.every((heading) => {
      if (!heading.heading.trim()) return false;
      return heading.subheadings.some(
        (sub) => sub.name.trim() && sub.definition.trim()
      );
    });

    if (!hasCompleteHeadings) {
      toast.error(
        "Please ensure all headings have at least one complete functional outcome with both name and definition"
      );
      return;
    }

    try {
      const checklistData = prepareChecklistData("PUBLISHED");
      await createChecklistMutation.mutateAsync(checklistData);

      toast.success("Checklist published successfully!");
      // Redirect to manage functional checklists page
      history.push("/admin/manage_functional_checklists");
    } catch (error) {
      console.error("Failed to publish checklist:", error);
      toast.error("Failed to publish checklist. Please try again.");
    }
  };

  return (
    <>
      <div className="container-fluid pt-12 pl-21 pb-12 pr-24 h-100 noScroll">
        <div className="card border-0 h-100">
          <div className="card-body mb-3">
            <div className="d-flex flex-wrap justify-content-between align-items-center">
              <h5 className="font-weight-lighter mb-3 mb-sm-3 mb-md-0 mb-lg-0">
                Admin: Create Custom Functional Checklist
              </h5>
            </div>
            <hr />

            <div className="noScroll h-90">
              {/* Checklist Title Input */}
              <div className="col-md-6 px-0 pt_1 pb-17">
                <div className="d-flex align-items-center">
                  <div className="position-relative">
                    <input
                      type="text"
                      id="name"
                      className="rounded pl-3 border w-358 h-35 rounded-5 fs-13"
                      placeholder="Enter Title for New Functional Checklist"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                    {!name && (
                      <span
                        className="position-absolute text-danger"
                        style={{
                          top: "2px",
                          left: "8px",
                          fontSize: "12px",
                          fontWeight: "bold",
                          pointerEvents: "none",
                          zIndex: 10,
                        }}
                      >
                        *
                      </span>
                    )}
                  </div>
                  <div className="icon-container position-relative ml-2">
                    <FaInfoCircle
                      size={16}
                      className=" cursor-pointer"
                      style={{ cursor: "pointer" }}
                    />
                    <div className="overlay shadow rounded w-236">
                      <div
                        className="my-2 pl-2"
                        style={{ width: "100%", height: "100%" }}
                      >
                        <small>Examples for naming CUSTOM Checklists:</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="noScroll h-90 verticalScroll pb-17">
                <Accordion
                  allowZeroExpanded
                  allowMultipleExpanded={false}
                  preExpanded={[currentUuid]}
                  className="mb-4 py-0"
                >
                  {headings.map((heading, headingIndex) => {
                    // Ensure orderIndex matches current position
                    heading.orderIndex = headingIndex;
                    return (
                      <div key={heading.id}>
                        <div className="chevron" />
                        <AccordionItem
                          className="mb-2"
                          uuid={headingIndex}
                          dangerouslySetExpanded={
                            currentUuid === headingIndex ? true : false
                          }
                        >
                          <AccordionItemHeading
                            className="accordion__primary"
                            onClick={() => {
                              setCurrentUuid(
                                headingIndex === currentUuid
                                  ? null
                                  : headingIndex
                              );
                            }}
                          >
                            <AccordionItemButton
                              style={{
                                height: 45,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "start",
                              }}
                            >
                              <div className="d-flex align-items-center ">
                                <input
                                  type="text"
                                  className="rounded pl-3 border-0 text_default w-270 h-35"
                                  placeholder="Enter Heading"
                                  value={heading.heading}
                                  onChange={(e) =>
                                    updateHeadingName(
                                      headingIndex,
                                      e.target.value
                                    )
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ fontSize: "13px" }}
                                />
                              </div>
                              {headings.length > 1 && (
                                <button
                                  className="btn btn-sm text-white ml-2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeHeading(headingIndex);
                                  }}
                                  title="Remove heading"
                                >
                                  <FaTrash size={12} />
                                </button>
                              )}
                            </AccordionItemButton>
                          </AccordionItemHeading>
                          <AccordionItemPanel>
                            {/* The scale every score under this heading is
                                recorded on. Left unset, the heading falls back
                                to the default scale. */}
                            <div className="d-flex align-items-center flex-wrap mb-3 pl-5 pt-3">
                              <small className="mr-2">Scoring Scale:</small>
                              <select
                                className="rounded pl-2 border w-270 h-35 fs-13"
                                value={heading.scoringScaleId}
                                onChange={(e) =>
                                  updateHeadingScoringScale(
                                    headingIndex,
                                    e.target.value
                                  )
                                }
                              >
                                <option value="">
                                  {DEFAULT_SCORING_SCALE_NAME} (default)
                                </option>
                                {scoringScales.map((scale) => (
                                  <option key={scale.id} value={scale.id}>
                                    {scale.name}
                                  </option>
                                ))}
                              </select>
                              {/* The values providers will be offered, drawn
                                  in the colours the scale defines. */}
                              <div className="d-flex align-items-center flex-wrap ml-3">
                                {getScaleValues(
                                  findScale(heading.scoringScaleId)
                                ).map((value) => {
                                  const pill = getScorePillProps(
                                    findScale(heading.scoringScaleId),
                                    value
                                  );
                                  return (
                                    <span
                                      key={value}
                                      className={`rounded-circle d-flex align-items-center justify-content-center font-weight-bold mr-1 ${pill.className}`}
                                      style={{
                                        width: "24px",
                                        height: "24px",
                                        fontSize: "11px",
                                        ...pill.style,
                                      }}
                                    >
                                      {value}
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                            {/* Functional Outcomes (Subheadings) */}
                            {heading.subheadings.map(
                              (subheading, subheadingIndex) => (
                                <div
                                  key={subheading.id}
                                  className={`row align-items-center ${
                                    subheadingIndex % 2 == 0 ? `bg_light` : ""
                                  } mb-3 h-50 pl-5`}
                                >
                                  <div className="col-lg-5 col-md-5 col-sm-12 ">
                                    <div className="d-flex align-items-center">
                                      <input
                                        type="text"
                                        className="rounded pl-3 border rounded w-270 h-35 fs-13"
                                        placeholder="Enter Functional Outcome"
                                        value={subheading.name}
                                        onChange={(e) =>
                                          updateSubheadingName(
                                            headingIndex,
                                            subheadingIndex,
                                            e.target.value
                                          )
                                        }
                                      />
                                      {heading.subheadings.length > 1 && (
                                        <button
                                          className="btn btn-sm text-danger ml-2"
                                          onClick={() =>
                                            removeFunctionalOutcome(
                                              headingIndex,
                                              subheadingIndex
                                            )
                                          }
                                          title="Remove functional outcome"
                                        >
                                          <FaTrash size={12} />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  <div className="col-lg-7 col-md-7 col-sm-12">
                                    <div className="d-flex align-items-center">
                                      <input
                                        className="rounded pl-3 border rounded w-270 h-35 fs-13"
                                        rows="3"
                                        placeholder="Enter Definition"
                                        value={subheading.definition}
                                        onChange={(e) =>
                                          updateSubheadingDefinition(
                                            headingIndex,
                                            subheadingIndex,
                                            e.target.value
                                          )
                                        }
                                      />
                                      <div className="icon-container position-relative ml-2">
                                        <FaInfoCircle
                                          size={16}
                                          className=" cursor-pointer"
                                          style={{ cursor: "pointer" }}
                                        />
                                        <div className="overlay shadow rounded w-236">
                                          <div
                                            className="my-2 pl-2"
                                            style={{
                                              width: "100%",
                                              height: "100%",
                                            }}
                                          >
                                            <small>
                                              Examples for naming CUSTOM
                                              Checklists:
                                            </small>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )
                            )}

                            {/* Add Another Functional Outcome Button */}
                            <div className="d-flex justify-content-start mb-3">
                              <button
                                className="btn btn-outline-primary d-flex align-items-center w-270 h-35 ml-5"
                                onClick={() =>
                                  addFunctionalOutcome(headingIndex)
                                }
                              >
                                <FaPlus size={12} className="mr-2" />
                                <small>Add Another Functional Outcome</small>
                              </button>
                            </div>
                          </AccordionItemPanel>
                        </AccordionItem>
                      </div>
                    );
                  })}
                </Accordion>

                {/* Add Another Heading Button */}
                <div className="">
                  <button
                    className="btn btn-outline-primary d-flex align-items-center w-100 h-45 rounded-0"
                    onClick={addHeading}
                  >
                    <FaPlus size={14} className="mr-2" />
                    <small>Add Another Heading</small>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer with Clear, Save for Later, and Publish buttons */}
      <FooterButton
        child1={
          <Item1 setIsOpen={setIsOpen} isOpen={isOpen} onClear={handleClear} />
        }
        child2={
          <Item2
            onSaveForLater={handleSaveForLater}
            onPublish={handlePublish}
            isPending={isPending || createChecklistMutation.isPending}
          />
        }
      />
    </>
  );
};

export default CreateCustomFunctionalChecklist;
